<template>
	<Teleport to="body">
		<div v-if="visible" class="pc-video-call-dialog__overlay">
			<div
				class="pc-video-call-dialog__panel"
				:class="{ 'pc-video-call-dialog__panel--fullscreen': fullscreen }"
				:style="panelStyle"
			>
				<div class="pc-video-call-dialog__header" @mousedown="startDrag">
					<div class="pc-video-call-dialog__title">
						{{ headerTitle }}
					</div>
					<div class="pc-video-call-dialog__header-actions">
						<el-button size="small" text @click="toggleFullscreen">
							{{ fullscreen ? "退出全屏" : "全屏" }}
						</el-button>
						<el-button size="small" text @click="zoomOut">-</el-button>
						<div class="pc-video-call-dialog__zoom">{{ Math.round(zoom * 100) }}%</div>
						<el-button size="small" text @click="zoomIn">+</el-button>
						<el-button size="small" text @click="close">关闭</el-button>
					</div>
				</div>

				<div class="pc-video-call-dialog__content" :style="{ transform: `scale(${zoom})` }">
			<div class="pc-video-call-dialog__stage">
				<div v-if="isAudioCall" class="pc-video-call-dialog__audio-detail">
					<div class="pc-video-call-dialog__audio-title">语音聊天详情</div>
					<div class="pc-video-call-dialog__audio-list">
					<div
						v-for="item in callUsers"
						:key="item.id"
						:class="[
							'pc-video-call-dialog__audio-item',
							{ 'pc-video-call-dialog__audio-item--speaking': isUserSpeaking(item.id) },
						]"
					>
							<img v-if="item.avatar" :src="item.avatar" class="pc-video-call-dialog__audio-avatar" />
							<div v-else class="pc-video-call-dialog__audio-avatar pc-video-call-dialog__audio-avatar--placeholder">
								{{ (item.name || item.id || "?").slice(0, 1).toUpperCase() }}
							</div>
							<div class="pc-video-call-dialog__audio-meta">
								<div class="pc-video-call-dialog__audio-name">{{ item.name || item.id }}</div>
								<div class="pc-video-call-dialog__audio-status">{{ item.statusText }}</div>
							</div>
						</div>
					</div>
				</div>
				<!-- 群聊接通后：多宫格 -->
				<div
					v-else-if="isGroupCall && status === 'call'"
					:class="[
						'pc-video-call-dialog__group-grid',
						{ 'pc-video-call-dialog__group-grid--with-main': hasMainTile },
					]"
				>
					<div
						v-for="(uid, index) in groupDisplayUserIds"
						:key="uid"
						:class="[
							'pc-video-call-dialog__group-item',
							{
								'pc-video-call-dialog__group-item--main': hasMainTile && index === 0,
								'pc-video-call-dialog__group-item--speaking': isUserSpeaking(uid),
							},
						]"
						@click="selectGroupUser(uid)"
					>
						<GRTCVideo :userId="uid" class="pc-video-call-dialog__video" />
						<div class="pc-video-call-dialog__group-tag">{{ uid }}</div>
					</div>
				</div>
				<template v-else>
				<!-- 主画面 -->
				<div class="pc-video-call-dialog__main" @dblclick="toggleFullscreen" @click="status==='ring' && !isAudioCall && startLocalPreview()">
					<!-- 来电阶段：优先用原生 getUserMedia 预览自己（避免 SDK 在无手势下黑屏） -->
					<video
						v-if="status === 'ring'"
						ref="localPreviewRef"
						class="pc-video-call-dialog__video"
						autoplay
						muted
						playsinline
					/>
					<!-- 接听后：使用 SDK 渲染 -->
					<GRTCVideo v-else-if="mainUserId" :userId="mainUserId" class="pc-video-call-dialog__video" />
					<div v-else class="pc-video-call-dialog__empty">
						<img v-if="peer.avatar" :src="peer.avatar" class="pc-video-call-dialog__ring-avatar" />
						<span v-else>等待接听...</span>
					</div>

					<div
						v-if="status === 'ring' && !localPreviewReady"
						class="pc-video-call-dialog__hint"
					>
						点击弹窗以开启本地预览
					</div>
				</div>

				<!-- 小窗：点击切换 -->
				<div class="pc-video-call-dialog__mini" @click="swapVideos">
					<GRTCVideo v-if="status === 'call' && miniUserId" :userId="miniUserId" class="pc-video-call-dialog__video" />
					<div v-else class="pc-video-call-dialog__mini-empty">点击切换</div>
				</div>
				</template>
			</div>

			<div class="pc-video-call-dialog__footer">
				<div class="pc-video-call-dialog__peer">
					<img v-if="peer.avatar" :src="peer.avatar" class="pc-video-call-dialog__avatar" />
					<div class="pc-video-call-dialog__meta">
						<div class="pc-video-call-dialog__name">{{ peer.name || "未知用户" }}</div>
						<div class="pc-video-call-dialog__status">{{ statusText }}</div>
					</div>
				</div>

				<div class="pc-video-call-dialog__controls">
					<template v-if="status === 'ring' && !isOutgoingRing">
						<el-button type="danger" plain @click="reject">拒绝</el-button>
						<el-button type="success" @click="accept">接听</el-button>
					</template>
					<el-button v-if="isOutgoingRing" type="danger" plain @click="hangup">
						取消
					</el-button>

					<el-button v-if="status === 'call'" :type="micMuted ? 'warning' : 'success'" plain @click="toggleMic">
						{{ micMuted ? "开启麦克风" : "关闭麦克风" }}
					</el-button>
					<el-button v-if="status === 'call'" type="danger" @click="hangup">挂断</el-button>
				</div>
			</div>
				</div>
			</div>
		</div>
	</Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import emitter from "@/utils/emitter";
import { getUserInfo } from "../sdk/utils.js";
import GRTCVideo from "../pages/rtc/grtc-video/grtc-video.vue";
import GoEasy from "goeasy";

const visible = ref(false);
const fullscreen = ref(false);
const zoom = ref(1);
const panelPos = ref({ left: 120, top: 80, width: 980, height: 680 });
let watchdogTimer = null;

const status = ref("ring"); // ring | call | ended
const callDirection = ref("incoming"); // incoming | outgoing
const callSnapshot = ref(null);
const micMuted = ref(false);
const selfUserId = ref("");
const selectedGroupUserId = ref("");
const localPreviewRef = ref(null);
const localStream = ref(null);
const localPreviewReady = ref(false);
const speakingUserIds = ref(new Set());
let speakingPollTimer = null;
const speakingAnalyserMap = new Map();

const peer = ref({ name: "", avatar: "" });
const self = ref({ id: "" });

const mediaText = computed(() => (isAudioCall.value ? "语音通话" : "视频通话"));
const headerTitle = computed(() => {
	if (status.value === "ring") return `来电 - ${mediaText.value}`;
	if (status.value === "call") return `${mediaText.value} - 通话中`;
	return `${mediaText.value}`;
});

const statusText = computed(() => {
	if (status.value === "ring") {
		return callDirection.value === "outgoing" ? "等待对方接听" : "等待接听";
	}
	if (status.value === "call") return "接听中";
	if (status.value === "ended") return "已结束";
	return "";
});
const isSelfCaller = computed(() => {
	const call = callSnapshot.value;
	if (!call?.caller?.id) return false;
	const currentUser = getUserInfo();
	const uid = selfUserId.value || currentUser?.userId || "";
	return Boolean(uid) && call.caller.id === uid;
});
const isOutgoingRing = computed(
	() => status.value === "ring" && (callDirection.value === "outgoing" || isSelfCaller.value),
);
const isAudioCall = computed(() => Number(callSnapshot.value?.mediaType) === 0);
const isGroupCall = computed(() => Boolean(callSnapshot.value?.groupId));
const isEndedUser = (u) => {
	const raw = String(u?.status || "").toLowerCase();
	return /(end|quit|reject|cancel|timeout|failed)/.test(raw);
};
const callUsers = computed(() => {
	const call = callSnapshot.value;
	if (!call) return [];
	const users = [];
	const buildStatusText = (u) => {
		const raw = String(u?.status || "").toLowerCase();
		if (raw.includes("accept") || raw.includes("call")) return "通话中";
		if (raw.includes("ring")) return "响铃中";
		if (raw.includes("reject")) return "已拒绝";
		if (raw.includes("end") || raw.includes("quit")) return "已结束";
		return statusText.value;
	};
	if (call?.caller?.id) {
		if (!isEndedUser(call.caller)) {
			users.push({
				id: call.caller.id,
				name: call.caller?.data?.nickname || call.caller?.data?.name || call.caller.id,
				avatar: call.caller?.data?.avatar || "",
				statusText: buildStatusText(call.caller),
			});
		}
	}
	(call?.callees || []).forEach((u) => {
		if (!u?.id) return;
		if (isEndedUser(u)) return;
		users.push({
			id: u.id,
			name: u?.data?.nickname || u?.data?.name || u.id,
			avatar: u?.data?.avatar || "",
			statusText: buildStatusText(u),
		});
	});
	return users;
});
const groupUserIds = computed(() => {
	const call = callSnapshot.value;
	if (!call) return [];
	const ids = [];
	if (call?.caller?.id && !isEndedUser(call.caller)) ids.push(call.caller.id);
	(call?.callees || []).forEach((u) => {
		if (u?.id && !isEndedUser(u)) ids.push(u.id);
	});
	return Array.from(new Set(ids));
});
const groupDisplayUserIds = computed(() => {
	// 限制为 9 宫格
	const ids = groupUserIds.value.slice(0, 9);
	if (!ids.length) return ids;
	const selected = selectedGroupUserId.value;
	if (!selected || !ids.includes(selected)) return ids;
	return [selected, ...ids.filter((id) => id !== selected)];
});
const hasMainTile = computed(() => groupDisplayUserIds.value.length >= 4);
const panelStyle = computed(() => {
	if (fullscreen.value) {
		return {
			left: "0px",
			top: "0px",
			width: "100vw",
			height: "100vh",
		};
	}
	return {
		left: `${panelPos.value.left}px`,
		top: `${panelPos.value.top}px`,
		width: `${panelPos.value.width}px`,
		height: `${panelPos.value.height}px`,
	};
});

const mainUserId = ref("");
const miniUserId = ref("");

function setUsersForCall(call) {
	const currentUser = getUserInfo();
	// 有些页面场景 localStorage 里可能还没写 goUserInfo，这里从 call 里兜底推断 selfId
	const guessedSelfId =
		currentUser?.userId ||
		call?.callees?.find?.((u) => u?.status?.toLowerCase?.() === "ringing")?.id ||
		call?.callees?.[0]?.id ||
		"";
	self.value = { id: guessedSelfId };
	selfUserId.value = guessedSelfId;
	const isCaller = call?.caller?.id === guessedSelfId;
	const peerUser = isCaller ? call?.callees?.[0] : call?.caller;

	peer.value = {
		name: peerUser?.data?.nickname || peerUser?.data?.name || peerUser?.id || "",
		avatar: peerUser?.data?.avatar || "",
	};

	// 来电阶段：先预览自己摄像头；接听后：主画面看对方，小窗是自己（微信风格）
	if (status.value === "ring") {
		mainUserId.value = guessedSelfId;
		miniUserId.value = "";
	} else {
		mainUserId.value = peerUser?.id || "";
		miniUserId.value = guessedSelfId;
	}

	// 同步麦克风状态（如果 call 上有的话）
	const selfFromCall =
		call?.caller?.id === guessedSelfId
			? call?.caller
			: call?.callees?.find?.((u) => u?.id === guessedSelfId) || call?.callees?.[0];
	if (typeof selfFromCall?.micMuted === "boolean") {
		micMuted.value = selfFromCall.micMuted;
	}
}

function openForRing(call, direction = "incoming") {
	callSnapshot.value = call;
	status.value = "ring";
	callDirection.value = direction;
	fullscreen.value = false;
	zoom.value = 1;
	setUsersForCall(call);
	selectedGroupUserId.value = "";
	visible.value = true;
	console.log("[pc-video-call-dialog] visible:", visible.value);
	startWatchdog();
	startSpeakingMonitor();
	localPreviewReady.value = false;
	// 不在这里立刻拉起摄像头：避免无用户手势被浏览器限制
}
function close() {
	visible.value = false;
	onClosed();
}

function onClosed() {
	stopWatchdog();
	stopSpeakingMonitor();
	stopLocalPreview();
	callSnapshot.value = null;
	status.value = "ring";
	callDirection.value = "incoming";
	mainUserId.value = "";
	miniUserId.value = "";
	peer.value = { name: "", avatar: "" };
	micMuted.value = false;
	fullscreen.value = false;
	zoom.value = 1;
	selectedGroupUserId.value = "";
}
function isUserSpeaking(userId) {
	return speakingUserIds.value.has(userId);
}
function selectGroupUser(uid) {
	selectedGroupUserId.value = uid;
}

function startWatchdog() {
	stopWatchdog();
	if (typeof window === "undefined") return;
	watchdogTimer = setInterval(() => {
		try {
			const call = window.GRTC?.currentCall?.();
			if (!call) {
				// 通话对象已不存在：直接关闭
				close();
				return;
			}
			callSnapshot.value = call;
			const s = String(call.status || "").toLowerCase();
			if (s.includes("ended") || s.includes("end") || s.includes("quit")) {
				close();
				return;
			}
		} catch (_) {}
	}, 500);
}

function stopWatchdog() {
	if (watchdogTimer) {
		clearInterval(watchdogTimer);
		watchdogTimer = null;
	}
}

function getMediaElByUserId(uid) {
	if (typeof document === "undefined" || !uid) return null;
	return (
		document.querySelector(`.grtc-video[userid="${uid}"] video`) ||
		document.querySelector(`.grtc-video[userid="${uid}"] audio`) ||
		document.querySelector(`.grtc-video[userId="${uid}"] video`) ||
		document.querySelector(`.grtc-video[userId="${uid}"] audio`) ||
		null
	);
}

function ensureAnalyser(uid) {
	if (typeof window === "undefined") return null;
	const media = getMediaElByUserId(uid);
	if (!media) return null;
	const existing = speakingAnalyserMap.get(uid);
	if (existing?.media === media) {
		existing.ctx?.resume?.().catch?.(() => {});
		return existing;
	}
	if (existing?.ctx) {
		try {
			existing.ctx.close();
		} catch (_) {}
	}
	const AudioCtx = window.AudioContext || window.webkitAudioContext;
	if (!AudioCtx) return null;
	try {
		const ctx = new AudioCtx();
		const analyser = ctx.createAnalyser();
		analyser.fftSize = 256;
		const source = ctx.createMediaElementSource(media);
		source.connect(analyser);
		const data = new Uint8Array(analyser.frequencyBinCount);
		const node = { ctx, analyser, media, data };
		speakingAnalyserMap.set(uid, node);
		ctx.resume?.().catch?.(() => {});
		return node;
	} catch (_) {
		return null;
	}
}

function updateSpeakingState() {
	if (!visible.value || status.value !== "call" || !isGroupCall.value) {
		if (speakingUserIds.value.size) speakingUserIds.value = new Set();
		return;
	}
	const currentIds = groupUserIds.value;
	const currentSet = new Set(currentIds);
	speakingAnalyserMap.forEach((entry, uid) => {
		if (!currentSet.has(uid)) {
			try {
				entry?.ctx?.close?.();
			} catch (_) {}
			speakingAnalyserMap.delete(uid);
		}
	});
	const active = new Set();
	currentIds.forEach((uid) => {
		const node = ensureAnalyser(uid);
		if (!node) return;
		node.analyser.getByteTimeDomainData(node.data);
		let sum = 0;
		for (let i = 0; i < node.data.length; i += 1) {
			const v = (node.data[i] - 128) / 128;
			sum += v * v;
		}
		const rms = Math.sqrt(sum / node.data.length);
		if (rms > 0.035) active.add(uid);
	});
	speakingUserIds.value = active;
}

function startSpeakingMonitor() {
	if (speakingPollTimer) return;
	speakingPollTimer = setInterval(updateSpeakingState, 300);
}

function stopSpeakingMonitor() {
	if (speakingPollTimer) {
		clearInterval(speakingPollTimer);
		speakingPollTimer = null;
	}
	speakingUserIds.value = new Set();
	speakingAnalyserMap.forEach((entry) => {
		try {
			entry?.ctx?.close?.();
		} catch (_) {}
	});
	speakingAnalyserMap.clear();
}

function swapVideos() {
	const t = mainUserId.value;
	mainUserId.value = miniUserId.value;
	miniUserId.value = t;
}

function accept() {
	if (typeof window === "undefined") return;
	window.GRTC
		?.accept?.()
		.then(() => {
			status.value = "call";
			const call = window.GRTC?.currentCall?.() || callSnapshot.value;
			if (call) setUsersForCall(call);
			// 接听后：停止本地预览（避免占用摄像头），并强制触发 SDK play（多次重试）
			stopLocalPreview();
			forcePlayAfterAccept();
		})
		.catch(() => {});
}

function reject() {
	if (typeof window === "undefined") return;
	window.GRTC?.end?.();
	status.value = "ended";
	setTimeout(() => (visible.value = false), 300);
}

function hangup() {
	if (typeof window === "undefined") return;
	window.GRTC?.end?.();
	status.value = "ended";
	setTimeout(() => (visible.value = false), 300);
}

function toggleMic() {
	if (typeof window === "undefined") return;
	const next = !micMuted.value;
	window.GRTC?.muteMic?.(next);
	micMuted.value = next;
}

function toggleFullscreen() {
	fullscreen.value = !fullscreen.value;
}
function startDrag(e) {
	if (fullscreen.value) return;
	const startX = e.clientX;
	const startY = e.clientY;
	const originLeft = panelPos.value.left;
	const originTop = panelPos.value.top;
	const onMove = (ev) => {
		panelPos.value.left = Math.max(0, originLeft + ev.clientX - startX);
		panelPos.value.top = Math.max(0, originTop + ev.clientY - startY);
	};
	const onUp = () => {
		window.removeEventListener("mousemove", onMove);
		window.removeEventListener("mouseup", onUp);
	};
	window.addEventListener("mousemove", onMove);
	window.addEventListener("mouseup", onUp);
}

function zoomIn() {
	zoom.value = Math.min(1.5, Math.round((zoom.value + 0.1) * 10) / 10);
}
function zoomOut() {
	zoom.value = Math.max(0.7, Math.round((zoom.value - 0.1) * 10) / 10);
}

function onUserMicChanged(event) {
	const currentUser = getUserInfo();
	if (event?.user?.id && currentUser?.userId && event.user.id !== currentUser.userId) return;
	if (typeof event?.muted === "boolean") micMuted.value = event.muted;
}
function onCallEnded() {
	// 仅 CALL_ENDED 代表整通电话结束，关闭弹窗
	status.value = "ended";
	setTimeout(() => close(), 500);
}
function onUserQuit() {
	const call = window.GRTC?.currentCall?.() || callSnapshot.value;
	if (!call) return;
	// USER_QUIT 只表示某个成员退出，不应关闭整个通话窗口
	callSnapshot.value = call;
	if (status.value !== "ring") {
		status.value = "call";
	}
}

async function startLocalPreview() {
	if (typeof window === "undefined") return;
	if (localStream.value) return;
	console.log("[pc-video-call-dialog] startLocalPreview");
	try {
		if (!navigator.mediaDevices?.getUserMedia) {
			console.log("[pc-video-call-dialog] getUserMedia not supported");
			return;
		}
		const stream = await navigator.mediaDevices.getUserMedia({
			video: true,
			audio: false,
		});
		localStream.value = stream;
		const el = localPreviewRef.value;
		if (el) {
			el.srcObject = stream;
			// 某些浏览器仍可能需要显式 play
			el.play().catch(() => {});
			localPreviewReady.value = true;
		}
	} catch (e) {
		console.log("[pc-video-call-dialog] getUserMedia failed", e);
	}
}

function stopLocalPreview() {
	try {
		if (localStream.value) {
			localStream.value.getTracks().forEach((t) => t.stop());
		}
	} catch (_) {}
	localStream.value = null;
	const el = localPreviewRef.value;
	if (el) {
		try {
			el.pause();
			el.srcObject = null;
		} catch (_) {}
	}
	localPreviewReady.value = false;
}

async function forcePlayAfterAccept() {
	if (typeof window === "undefined") return;
	await nextTick();
	const call = window.GRTC?.currentCall?.() || callSnapshot.value;
	if (!call) return;
	const peerId =
		(call?.caller?.id === selfUserId.value ? call?.callees?.[0]?.id : call?.caller?.id) || "";
	const selfId = selfUserId.value;
	// 注意：GRTC.play() 无参会导致 SDK 内部 getView(undefined) 返回空，从而触发 MutationObserver.observe 非 Node
	[0, 200, 500, 900].forEach((delay) => {
		setTimeout(() => {
			try {
				if (selfId) {
					const p = window.GRTC?.play?.(selfId);
					p?.catch?.((e) => console.log("[pc-video-call-dialog] play self rejected", e));
				}
			} catch (e) {
				console.log("[pc-video-call-dialog] play self failed", e);
			}
			try {
				if (peerId) {
					const p = window.GRTC?.play?.(peerId);
					p?.catch?.((e) => console.log("[pc-video-call-dialog] play peer rejected", e));
				}
			} catch (e) {
				console.log("[pc-video-call-dialog] play peer failed", e);
			}
		}, delay);
	});
}
function onUserAccepted() {
	const call = window.GRTC?.currentCall?.() || callSnapshot.value;
	if (!call) return;
	callSnapshot.value = call;
	status.value = "call";
	setUsersForCall(call);
	if (isGroupCall.value && !selectedGroupUserId.value) {
		selectedGroupUserId.value = groupUserIds.value[0] || "";
	}
	forcePlayAfterAccept();
	setTimeout(() => updateSpeakingState(), 400);
}

function bindGrtcEvents() {
	if (typeof window === "undefined") return;
	const GRTC = window.GRTC;
	if (!GRTC?.on) return;
	GRTC.on(GRTC.EVENT.USER_MIC_CHANGED, onUserMicChanged);
	GRTC.on(GRTC.EVENT.CALL_ENDED, onCallEnded);
	GRTC.on(GRTC.EVENT.USER_QUIT, onUserQuit);
	GRTC.on(GRTC.EVENT.USER_ACCEPTED, onUserAccepted);
}
function unbindGrtcEvents() {
	if (typeof window === "undefined") return;
	const GRTC = window.GRTC;
	if (!GRTC?.off) return;
	GRTC.off(GRTC.EVENT.USER_MIC_CHANGED, onUserMicChanged);
	GRTC.off(GRTC.EVENT.CALL_ENDED, onCallEnded);
	GRTC.off(GRTC.EVENT.USER_QUIT, onUserQuit);
	GRTC.off(GRTC.EVENT.USER_ACCEPTED, onUserAccepted);
}

const onPcVideoRing = (payload) => {
	if (typeof window === "undefined") return;
	const call = payload?.call || window.GRTC?.currentCall?.();
	if (!call) return;
	console.log("[pc-video-call-dialog] onPcVideoRing by emitter", call);
	openForRing(call, "incoming");
};
const onPcVideoOutgoing = (payload) => {
	if (typeof window === "undefined") return;
	const call = payload?.call || window.GRTC?.currentCall?.();
	if (!call) return;
	openForRing(call, "outgoing");
};
const onPcAudioOpen = (payload) => {
	if (typeof window === "undefined") return;
	const call = payload?.call || window.GRTC?.currentCall?.();
	if (!call) return;
	openForRing(call, payload?.direction || "incoming");
};
const onPcVideoRingByWindowEvent = (event) => {
	if (typeof window === "undefined") return;
	const call = event?.detail?.call || window.GRTC?.currentCall?.();
	if (!call) return;
	console.log("[pc-video-call-dialog] onPcVideoRing by window", call);
	openForRing(call);
};

let grtcBound = false;
let bindRetryTimer = null;
function tryBindGrtcEvents() {
	if (grtcBound) return;
	try {
		// goeasy-rtc 会在未连接时抛错，这里先判断连接状态
		if (GoEasy?.getConnectionStatus?.() !== "connected") return;
		bindGrtcEvents();
		grtcBound = true;
	} catch (e) {
		// 保持静默重试，避免 mounted hook 警告
	}
}
function startBindRetry() {
	if (bindRetryTimer) return;
	bindRetryTimer = setInterval(() => {
		tryBindGrtcEvents();
		if (grtcBound) {
			clearInterval(bindRetryTimer);
			bindRetryTimer = null;
		}
	}, 800);
}
function stopBindRetry() {
	if (bindRetryTimer) {
		clearInterval(bindRetryTimer);
		bindRetryTimer = null;
	}
}

onMounted(() => {
	try {
		tryBindGrtcEvents();
		startBindRetry();
		window.addEventListener("grtc:pc-video-ring", onPcVideoRingByWindowEvent);
		emitter.on("goeasy:connected", tryBindGrtcEvents);
	} catch (e) {
		console.error("[pc-video-call-dialog] mounted hook error", e);
	}
});

// 来电触发可能早于 mounted（比如连接初始化很早），这里提前注册避免错过
emitter.on("grtc:pc-video-ring", onPcVideoRing);
emitter.on("grtc:pc-video-outgoing", onPcVideoOutgoing);
emitter.on("grtc:pc-audio-open", onPcAudioOpen);

onBeforeUnmount(() => {
	emitter.off("grtc:pc-video-ring", onPcVideoRing);
	emitter.off("grtc:pc-video-outgoing", onPcVideoOutgoing);
	emitter.off("grtc:pc-audio-open", onPcAudioOpen);
	emitter.off("goeasy:connected", tryBindGrtcEvents);
	window.removeEventListener("grtc:pc-video-ring", onPcVideoRingByWindowEvent);
	unbindGrtcEvents();
	stopBindRetry();
});
</script>

<style scoped>
.pc-video-call-dialog__overlay {
	position: fixed;
	inset: 0;
	z-index: 30000;
	pointer-events: none;
}
.pc-video-call-dialog__panel {
	position: fixed;
	background: #fff;
	border-radius: 12px;
	box-shadow: 0 20px 50px rgba(0, 0, 0, 0.35);
	overflow: hidden;
	min-width: 680px;
	min-height: 420px;
	resize: both;
	pointer-events: auto;
}
.pc-video-call-dialog__panel--fullscreen {
	border-radius: 0;
	resize: none;
}
.pc-video-call-dialog__header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	width: 100%;
	padding: 12px 16px;
	border-bottom: 1px solid #ebeef5;
	cursor: move;
	box-sizing: border-box;
}
.pc-video-call-dialog__title {
	font-weight: 600;
	font-size: 14px;
	color: #303133;
}
.pc-video-call-dialog__header-actions {
	display: flex;
	align-items: center;
	gap: 6px;
}
.pc-video-call-dialog__zoom {
	min-width: 52px;
	text-align: center;
	color: #606266;
	font-size: 12px;
}
.pc-video-call-dialog__content {
	transform-origin: top left;
	padding: 12px 16px 14px;
	box-sizing: border-box;
	height: calc(100% - 58px);
}
.pc-video-call-dialog__stage {
	position: relative;
	width: 100%;
	height: calc(100% - 58px);
	background: #0f0f10;
	border-radius: 10px;
	overflow: hidden;
}
.pc-video-call-dialog__audio-detail {
	height: 100%;
	display: flex;
	flex-direction: column;
	color: #fff;
	padding: 14px;
	box-sizing: border-box;
}
.pc-video-call-dialog__audio-title {
	font-size: 14px;
	font-weight: 600;
	margin-bottom: 10px;
}
.pc-video-call-dialog__audio-list {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 10px;
	overflow: auto;
	padding-right: 4px;
}
.pc-video-call-dialog__audio-item {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 10px;
	background: rgba(255, 255, 255, 0.06);
	border-radius: 10px;
}
.pc-video-call-dialog__audio-item--speaking {
	background: rgba(54, 215, 144, 0.16);
	box-shadow: 0 0 0 1px rgba(54, 215, 144, 0.85), 0 0 12px rgba(54, 215, 144, 0.35);
}
.pc-video-call-dialog__audio-avatar {
	width: 38px;
	height: 38px;
	border-radius: 50%;
	object-fit: cover;
	flex: none;
}
.pc-video-call-dialog__audio-avatar--placeholder {
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 15px;
	background: rgba(255, 255, 255, 0.22);
}
.pc-video-call-dialog__audio-meta {
	min-width: 0;
}
.pc-video-call-dialog__audio-name {
	font-size: 13px;
	font-weight: 500;
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
}
.pc-video-call-dialog__audio-status {
	font-size: 12px;
	margin-top: 4px;
	color: rgba(255, 255, 255, 0.8);
}
.pc-video-call-dialog__group-grid {
	width: 100%;
	height: 100%;
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	grid-auto-rows: 1fr;
	gap: 8px;
	padding: 8px;
	box-sizing: border-box;
	align-content: start;
}
.pc-video-call-dialog__group-item {
	position: relative;
	border-radius: 10px;
	overflow: hidden;
	background: #111;
	aspect-ratio: 1 / 1;
	cursor: pointer;
	border: 1px solid rgba(255, 255, 255, 0.08);
}
.pc-video-call-dialog__group-item--speaking {
	border-color: rgba(54, 215, 144, 0.95);
	box-shadow: 0 0 0 1px rgba(54, 215, 144, 0.9), 0 0 16px rgba(54, 215, 144, 0.4);
}
.pc-video-call-dialog__group-grid--with-main .pc-video-call-dialog__group-item--main {
	grid-column: span 2;
	grid-row: span 2;
	aspect-ratio: auto;
}
.pc-video-call-dialog__group-tag {
	position: absolute;
	left: 6px;
	bottom: 6px;
	max-width: calc(100% - 12px);
	padding: 2px 6px;
	border-radius: 6px;
	font-size: 11px;
	color: rgba(255, 255, 255, 0.92);
	background: rgba(0, 0, 0, 0.35);
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	pointer-events: none;
}
.pc-video-call-dialog__main {
	position: absolute;
	inset: 0;
}
.pc-video-call-dialog__mini {
	position: absolute;
	right: 14px;
	top: 14px;
	width: 220px;
	height: 150px;
	border-radius: 10px;
	overflow: hidden;
	border: 1px solid rgba(255, 255, 255, 0.18);
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
	cursor: pointer;
	background: rgba(0, 0, 0, 0.4);
}
.pc-video-call-dialog__video {
	width: 100%;
	height: 100%;
}
.pc-video-call-dialog__hint {
	position: absolute;
	left: 16px;
	bottom: 16px;
	padding: 6px 10px;
	border-radius: 10px;
	background: rgba(0, 0, 0, 0.35);
	color: rgba(255, 255, 255, 0.9);
	font-size: 12px;
	z-index: 3;
}
.pc-video-call-dialog__empty,
.pc-video-call-dialog__mini-empty {
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-direction: column;
	color: rgba(255, 255, 255, 0.7);
	font-size: 13px;
}
.pc-video-call-dialog__ring-avatar {
	width: 112px;
	height: 112px;
	border-radius: 12px;
	object-fit: cover;
	background: rgba(255, 255, 255, 0.15);
}
.pc-video-call-dialog__footer {
	margin-top: 12px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
}
.pc-video-call-dialog__peer {
	display: flex;
	align-items: center;
	gap: 10px;
	min-width: 0;
}
.pc-video-call-dialog__avatar {
	width: 34px;
	height: 34px;
	border-radius: 8px;
	object-fit: cover;
	background: #f2f3f5;
}
.pc-video-call-dialog__meta {
	min-width: 0;
}
.pc-video-call-dialog__name {
	font-size: 13px;
	font-weight: 600;
	color: #303133;
	line-height: 18px;
	max-width: 320px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
.pc-video-call-dialog__status {
	margin-top: 2px;
	font-size: 12px;
	color: #606266;
	line-height: 16px;
}
.pc-video-call-dialog__controls {
	display: flex;
	align-items: center;
	gap: 10px;
}
</style>

